
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * CustomProgressBar
 *
 * @file CustomProgressBar.jsx
 * @author Mohamed yasar arabath M
 */


import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';

const useStyles = makeStyles({
  root: {
    position: 'relative',
    background: '#06111f',
    borderRadius: 999,
    height: (props) => props.height,
    minHeight: 12,
    width: '100%',
    padding: 2,
    display: 'flex',
    boxSizing: 'border-box'
  },
  bar: {
    borderRadius: '999px 0 0 999px'
  },
  bar1Buffer: {
    width: (props) => `${props.value}%`, // 8px gap on the trailing side
    background: '#18d6d6',
    zIndex: 2,
    borderRadius: (props) => (props.value >= 100 ? '999px' : '999px 0 0 999px')
  },
  gap: {
    width: (props) => props.gap,
    background: '#06111f',
    zIndex: 3
  },
  bar2Buffer: {
    left: (props) => `calc(${props.value}% + ${props.gap}px)`, // 8px gap to the right side of bar1Buffer
    width: (props) => `calc(${props.bufferValue}% - ${props.gap}px)`,
    backgroundImage: 'repeating-linear-gradient(135deg, #f57a89 0px, #f57a89 12px, #ffb6b6 12px, #ffb6b6 24px)',
    zIndex: 1,
    borderRadius: (props) => {
      if (props.value) {
        return 'unset';
      }

      return props.bufferValue >= 100 ? '999px' : '999px 0 0 999px';
    }
  }
});

const CustomProgressBar = memo(({ value, bufferValue, gap, height }) => {
  const classes = useStyles({ value, bufferValue, gap, height });

  return (
    <div className={classes.root}>
      {value ? <div className={`${classes.bar} ${classes.bar1Buffer}`} /> : null}
      {value && bufferValue ? <div className={classes.gap} /> : null}
      {bufferValue ? <div className={`${classes.bar} ${classes.bar2Buffer}`} /> : null}
    </div>
  );
});

CustomProgressBar.defaultProps = {
  gap: 0,
  height: 12,
  value: 0,
  bufferValue: 0
};

CustomProgressBar.propTypes = {
  value: PropTypes.number,
  bufferValue: PropTypes.number,
  gap: PropTypes.number,
  height: PropTypes.number
};

export default CustomProgressBar;
